import { Injectable } from '@angular/core'
import { HubConnectionState, HubConnection, HubConnectionBuilder, HttpTransportType, LogLevel } from '@microsoft/signalr'
import { Store } from '@ngrx/store'
import { Logger } from 'angular2-logger/core'
import { of } from 'rxjs/observable/of'
import { _throw } from 'rxjs/observable/throw'
import {
  distinctUntilChanged, filter, share, tap
} from 'rxjs/operators'
import { environment } from '../../../environments/environment'

import * as Chat from '../../chat/store/actions'
import * as App from '../../store/actions/app.actions'
import * as authActions from '../auth/store/actions/auth.actions'
import * as authSelectors from './../auth/store/selectors/auth.selectors'
import * as signalActions from './store/actions/signalR.actions'
import * as Signal from './store/reducers/signalR.reducer'
import * as fromTeam from '../team'

import { LoginTokenModel } from '../../api/identity/models/login-token-model'
import { JeanMessageConfirmationType } from '../conversation'
import { AppState } from '../../store'
import { HashSet } from '../utils/hashset'
import {
  ClientEvent,
  ClientMessage,
  ConversationAssignedIE,
  ConversationClosedIE,
  ConversationOpenIE,
  MessageConfirmation,
  VendorEvent,
  VendorEventRequest,
  VendorMessage,
  VendorMessageRequest, TeamMemberCreatedIE, TeamMemberDeletedIE, VendorTeamCreatedIE, VendorTeamUpdatedIE
} from './models'
import { EventType } from './models/events'

import { TypingService } from './typing.service'
import { UserV2Service } from '../../api/identity/services/user-v2.service'
import { UpdatedTeamEvent } from '../team'
import { VendorTeamActivatedIE, VendorTeamDeactivatedIE } from './models/integration-events/vendor-team-active-ie'

@Injectable()
export class SignalRService {
  private state: LoginTokenModel
  private signal$ = this.store.select(state => state.signal).pipe(share())
  private auth$ = this.store.select(authSelectors.getAuthState).pipe(
    distinctUntilChanged(),
    filter((state: LoginTokenModel) => !!(state && state.access_token)),
    tap((state: LoginTokenModel) => this.updateAuth(state)),
    share())
  private loggedIn$ = this.store.select(authSelectors.getAuthLoggedIn).pipe(share())
  private connectionStatus
  private connection: HubConnection
  private isLoggedIn = false
  private vendorEventsToSend: HashSet<VendorEventRequest> = new HashSet<VendorEventRequest>('sent')
  private vendorMessagesToSend: HashSet<VendorMessageRequest> = new HashSet<VendorMessageRequest>('id')
  private msgsUnconfirmed: HashSet<VendorMessageRequest> = new HashSet<VendorMessageRequest>('id')
  private msgsUndelivered: HashSet<VendorMessageRequest> = new HashSet<VendorMessageRequest>('id')

  private autoRefreshId: any
  private isAutorefreshOn = false
  private isRefreshingToken = false

  constructor(private logger: Logger,
              private store: Store<AppState>,
              private authService: UserV2Service,
              private typingService: TypingService) {
    this.signal$
      .subscribe((state: Signal.State) => {
        this.connection = state.connection
        this.connectionStatus = state.status
      })

    this.auth$.subscribe(_ => _)
    this.loggedIn$.subscribe((loggedIn: boolean) => this.isLoggedIn = loggedIn)
  }

  get isConnected() {
    return this.connectionStatus === HubConnectionState.Connected
  }

  get isConnecting() {
    return this.connectionStatus === HubConnectionState.Connecting
  }

  public startAutoRefreshingToken() {
    if (this.isAutorefreshOn) {
      return
    }

    this.autoRefreshId = setInterval(() => {
      this.store.dispatch(new authActions.RefreshToken())
    }, 270000)
    this.isAutorefreshOn = true
  }

  public stopAutoRefreshingToken() {
    if (!this.autoRefreshId) {
      return
    }

    clearInterval(this.autoRefreshId)
    this.autoRefreshId = null
    this.isAutorefreshOn = false
  }

  public sendVendorMessage(message: VendorMessageRequest): void {
    this.logger.info(`sendVendorMessage: ${JSON.stringify(message)}`)
    if (!this.isConnected) {
      this.vendorMessagesToSend.add(message)
      if (!this.isConnecting) {
        this.startConnection()
      }
    } else {
      this.msgsUnconfirmed.add(message)
      this.connection.invoke('sendVendorMessage', message)
        .then(_ => {
          this.vendorMessagesToSend.remove(message)
          this.logger.info(`vendorMessagesToSend left: ${this.vendorMessagesToSend.size()}`)
        })
        .catch(() => this.handleSendMessageFail(message))
    }
  }

  public sendVendorEvent(event: VendorEventRequest): void {
    this.logger.info(`sendVendorEvent: ${EventType[event.eventType]} ${JSON.stringify(event)}`)
    if (!this.isConnected) {
      this.vendorEventsToSend.add(event)
      if (!this.isConnecting) {
        this.startConnection()
      }
    } else {
      this.connection.invoke('sendVendorEvent', event)
        .then(_ => {
          this.vendorEventsToSend.remove(event)
          this.logger.info(`vendorEventsToSend left: ${this.vendorEventsToSend.size()}`)
        })
        .catch(() => this.handleSendVendorEventFail(event))
    }
  }

  public startConnection(): void {
    if (this.isConnecting || this.isConnected || !this.isLoggedIn) {
      return
    }

    this.store.dispatch(
      new signalActions.ChangeStatus({ status: HubConnectionState.Connecting })
    )

    this.logger.info(`[SIGNALR] building new connection`)

    this.connection = new HubConnectionBuilder()
      .withUrl(environment.signalR.url, {
        transport: HttpTransportType.WebSockets,
        accessTokenFactory: () => this.state.access_token,
      })
      .withAutomaticReconnect([3000])
      .configureLogging(LogLevel.Trace)
      .build()

      this.logger.info(`[SIGNALR] starting connection`, this.connection)

      this.connection.start()
        .then(() => {
          this.logger.info(`[SIGNALR] Now connected`)
          this.store.dispatch(new signalActions.ChangeStatus({status: HubConnectionState.Connected, connection: this.connection }))

          this.listenForConnectionStatuses()
          this.listenSimpleTopics()
          this.listenForMessageConfirmation()

          this.vendorEventsToSend.forEach(e => this.sendVendorEvent(e))
          this.vendorMessagesToSend.forEach(m => this.sendVendorMessage(m))
          this.logger.info('[SIGNALR] SUC', this.connection)
        }).catch(err => {
          this.logger.error('Error while starting connection: ' + JSON.stringify(err))
          if (err.statusCode === 401) {
            this.refreshTokenAndReconnect()
          } else {
            this.store.dispatch(new signalActions.ChangeStatus({status: HubConnectionState.Disconnected }))
            this.logger.error(`Error occured during signalR connection, err: ${err}`)
          }
        })
  }

  public async stopConnection(): Promise<void> {
    if (this.connection) {
      await this.connection.stop()
    }
  }

  public reconnect() {
    if (this.connection) {
      this.connection.stop().then(() => {
        this.logger.info('[SIGNALR] connection stopped', this.connection)
        this.startConnection()
      }).catch(err => {
        this.logger.error('[SIGNALR] Error while stopping connection: ' + JSON.stringify(err))
      })
    }
    else {
      this.startConnection()
    }
  }

  private listenSimpleTopics(): void {
    // messages and events
    this.listenForEvent<ClientMessage>('notifyOnClientMessageReceived', this.clientMessageReceivedHandler.bind(this))
    this.listenForEvent<ClientEvent>('notifyOnClientEventReceived', this.clientEventReceivedHandler.bind(this))
    this.listenForEvent<VendorMessage>('notifyOnVendorMessageReceived', this.vendorMessageReceivedHandler.bind(this))
    this.listenForEvent<VendorEvent>('notifyOnVendorEventReceived', this.vendorEventReceivedHandler.bind(this))

    // Integration events
    this.listenForEvent<ConversationAssignedIE>('notifyOnConversationAssigned', this.conversationAssignedHandler.bind(this))
    this.listenForEvent<ConversationClosedIE>('notifyOnConversationClosed', this.conversationClosedHandler.bind(this))
    this.listenForEvent<ConversationOpenIE>('notifyOnConversationOpen', this.conversationOpenHandler.bind(this))
    this.listenForEvent<TeamMemberCreatedIE>('notifyOnTeamMemberCreated', this.teamMemberCreatedHandler.bind(this))
    this.listenForEvent<TeamMemberDeletedIE>('notifyOnTeamMemberDeleted', this.teamMemberDeletedHandler.bind(this))
    this.listenForEvent<VendorTeamCreatedIE>('notifyOnVendorTeamCreated', this.vendorTeamCreatedHandler.bind(this))
    this.listenForEvent<VendorTeamUpdatedIE>('notifyOnVendorTeamUpdated', this.vendorTeamUpdatedHandler.bind(this))
    this.listenForEvent<VendorTeamActivatedIE>('notifyOnVendorTeamActivated', this.vendorTeamActivatedHandler.bind(this))
    this.listenForEvent<VendorTeamDeactivatedIE>('notifyOnVendorTeamDeactivated', this.vendorTeamDeactivatedHandler.bind(this))
  }

  private listenForEvent<T>(topic: string, handler) {
    this.connection.on(topic, handler)
  }

  private clientMessageReceivedHandler(message: ClientMessage) {
    this.logger.info('[SIGNALR] clientMessageReceivedHandler', message)
    this.store.dispatch(new fromTeam.NewMessage({vendorTeamId: message.vendorTeamId, newMessage: true}))
    this.store.dispatch(new Chat.ReceiveMessage({
      conversationId: message.conversationId,
      message: {
        messageText: message.messageText,
        isSentByClient: true,
        senderName: message.senderName,
        id: message.id,
        conversationId: message.conversationId,
        sent: message.sent,
        messageType: message.messageType as any,

        soundUrl: message.soundUrl,
        vendorTeamId: message.vendorTeamId,
        confirmed: JeanMessageConfirmationType.Ack,
        conversation: message.conversation,
        isAutomatic: false,
      },
    }))
  }

  private vendorMessageReceivedHandler(message: VendorMessage) {
    this.store.dispatch(new Chat.ReceiveMessage({
      conversationId: message.conversationId,
      message: {
        ...message,
        isSentByClient: false,
        messageType: message.messageType as any,
        confirmed: JeanMessageConfirmationType.Ack,
      },
    }))
  }

  private clientEventReceivedHandler(event: ClientEvent) {
    const newEvent: any = {...event}
    const typingUser = {
      id: newEvent.commodityClientId || newEvent.visitorId,
      conversationId: newEvent.conversationId,
    }

    switch (newEvent.eventType) {
      case EventType.Typing: {
        this.typingService.startTyping(typingUser, true)
        break
      }
      case EventType.EndOfTyping: {
        this.typingService.stopTyping(typingUser, true)
        break
      }
      case EventType.LastMessageSeen: {
        this.store.dispatch(new Chat.LastMessageSeen({event: newEvent, isSentByClient: true}))
        break
      }
    }
  }


  private vendorEventReceivedHandler(event: VendorEvent) {
    const newEvent: any = {...event}
    const typingUser = {
      id: newEvent.vendorUserId,
      conversationId: newEvent.conversationId,
      vendorUserId: newEvent.vendorUserId,
      vendorName: newEvent.senderName
    }

    switch (newEvent.eventType) {
      case EventType.Typing: {
        this.typingService.startTyping(typingUser, false)
        break
      }
      case EventType.EndOfTyping: {
        this.typingService.stopTyping(typingUser, false)
        break
      }
      case EventType.LastMessageSeen: {
        this.store.dispatch(new Chat.LastMessageSeen({event: newEvent, isSentByClient: false}))
        if (newEvent.vendorTeamId) {
          this.store.dispatch(new fromTeam.NewMessage({vendorTeamId: newEvent.vendorTeamId, newMessage: false}))
        }
        break
      }
    }
  }

  private conversationAssignedHandler(event: ConversationAssignedIE) {
    this.store.dispatch(new Chat.ConversationAssignedEvent({event}))
  }

  private conversationClosedHandler(event: ConversationClosedIE) {
    this.store.dispatch(new Chat.ConversationClosedEvent({event}))
  }

  private conversationOpenHandler(event: ConversationOpenIE) {
    this.store.dispatch(new Chat.ConversationOpenEvent({event}))
  }

  private teamMemberCreatedHandler(event: TeamMemberCreatedIE) {
    this.store.dispatch(new Chat.TeamMemberCreatedEvent({event}))
  }

  private teamMemberDeletedHandler(event: TeamMemberDeletedIE) {
    this.store.dispatch(new Chat.TeamMemberDeletedEvent({event}))
  }

  private vendorTeamCreatedHandler(event: VendorTeamCreatedIE) {
    this.store.dispatch(new Chat.VendorTeamCreatedEvent({event}))
  }

  private vendorTeamUpdatedHandler(event: VendorTeamUpdatedIE) {
    this.store.dispatch(new UpdatedTeamEvent({event}))
  }

  private vendorTeamActivatedHandler(event: VendorTeamActivatedIE) {
    this.store.dispatch(new Chat.VendorTeamActivatedEvent({event: event}))
  }

  private vendorTeamDeactivatedHandler(event: VendorTeamDeactivatedIE) {
    this.store.dispatch(new Chat.VendorTeamDeactivatedEvent({event: event}))
  }

  private listenForMessageConfirmation(): void {
    const topic = 'notifyOnMessageConfirmation'

    this.connection.on(topic, (confirmation: MessageConfirmation) => {
      this.removeFromUnconfirmed(confirmation)

      if (!confirmation.isAcked) {
        // msg undelivered
        const unconfirmed = this.msgsUnconfirmed.getById(confirmation.id)
        if (unconfirmed) {
          this.msgsUndelivered.add(unconfirmed)
        }
      } else {
        // msg delivered
        this.msgsUndelivered.removeById(confirmation.id)
      }

      this.confirmMessage(confirmation)
    })
  }

  private confirmMessage(confirmation: MessageConfirmation) {
    this.store.dispatch(new Chat.SetMessageConfirmation({
      messageId: confirmation.id,
      confirmationType: confirmation.isAcked ? JeanMessageConfirmationType.Ack : JeanMessageConfirmationType.Nack,
    }))
  }

  private listenForConnectionStatuses(): void {
    const dispatchConnectionStatus = () => {
      this.logger.info(`Connection status: ${JSON.stringify(this.connection.state)}`)
      this.store.dispatch(new signalActions.ChangeStatus({status: this.connection.state}))
    }

    this.connection.onclose(dispatchConnectionStatus)
    this.connection.onreconnected(dispatchConnectionStatus)
    this.connection.onreconnecting(dispatchConnectionStatus)
  }

  private refreshTokenAndReconnect() {
    if (this.isRefreshingToken) {
      return
    }

    this.isRefreshingToken = true
    this.store.dispatch(new signalActions.ChangeStatus({status: HubConnectionState.Reconnecting }))
    this.authService.RefreshToken().subscribe((authState) => {
          this.store.dispatch(new authActions.RefreshTokenSuccess(authState))
          this.updateAuth(authState)
          this.reconnect()
          this.isRefreshingToken = false
    }, (refreshError) => {
          // If there is an exception calling 'refreshToken', bad news so logout.
          this.store.dispatch(new App.ApiCallFailure({
            title: 'Error',
            msg: 'Could not refresh token, please log in.'
          }))
          this.store.dispatch(new authActions.Logout({}))
          this.store.dispatch(new signalActions.ChangeStatus({status: HubConnectionState.Disconnected}))
          return of()
      })
  }

  private handleSendMessageFail(message: VendorMessageRequest) {
    this.confirmMessage({
      id: message.id,
      conversationId: message.conversationId,
      isAcked: false
    })

    this.removeFromUnconfirmed(message)
    const unconfirmed = this.msgsUnconfirmed.getById(message.id)
    if (unconfirmed) {
      this.msgsUndelivered.add(unconfirmed)
    }
  }

  private handleSendVendorEventFail(event: VendorEventRequest) {
    this.vendorEventsToSend.add(event)
  }

  private removeFromUnconfirmed(data: VendorMessageRequest | MessageConfirmation) {
    this.msgsUnconfirmed.removeById(data.id)
  }

  private updateAuth(authState: LoginTokenModel) {
    this.state = authState

    if (this.connection) {
      this.store.dispatch(new signalActions.ChangeStatus({status: HubConnectionState.Connected, connection: this.connection}))
    }
  }
}




// WEBPACK FOOTER //
// ./src/app/shared/signalR/signalR.service.ts